import THEME from "../theme";

/**
 * Focal species and habitats for West Coast oceanic data center governance.
 *
 * Temperature thresholds sourced from:
 *   - EPA Region 10 Guidance (EPA 910-B-03-002, April 2003)
 *   - Brett (1952) — Temperature tolerance in young Pacific salmon
 *   - Eliason et al. (2011, Science) — Sockeye population CTmax variation
 *   - Mayer et al. (2024, Fish and Fisheries) — Thermal tolerance systematic review
 *   - CCME Marine Temperature Guidelines
 *   - Thom et al. (2014, J. Exp. Mar. Biol.) — Eelgrass PNW thermal response
 *   - Supratya et al. (2020, J. Phycology) — Bull kelp thermal plasticity
 */

const SPECIES_DATA = [
  {
    id: "chinook",
    name: "Chinook Salmon",
    scientific: "Oncorhynchus tshawytscha",
    icon: "🐟",
    color: THEME.salmon,
    status: "ESA Threatened/Endangered (multiple DPS)",
    optimalRange: [10, 16],
    stressOnset: 18,
    lethalTemp: 25.1,
    spawningMax: 13,
    migrationBlock: 21,
    thresholds: [
      { label: "Spawning/Egg", max: 13, color: "#22d3ee" },
      { label: "Juvenile Rearing", max: 16, color: "#34d399" },
      { label: "Migration", max: 20, color: "#f59e0b" },
      { label: "Stress/Disease", max: 23, color: "#ef4444" },
      { label: "Lethal (UILT)", max: 25.1, color: "#991b1b" },
    ],
    keyFacts: [
      "7-DADMax spawning criterion: 13°C (EPA Region 10)",
      "Disease mortality approaches 100% at ≥20.6°C",
      "Heat shock proteins produced at 20°C",
      "Migration ceases at 21–22°C",
      "86% summer ocean habitat loss projected under medium emissions",
    ],
  },
  {
    id: "coho",
    name: "Coho Salmon",
    scientific: "Oncorhynchus kisutch",
    icon: "🐟",
    color: "#fb7185",
    status: "ESA Threatened (multiple ESUs)",
    optimalRange: [9, 16],
    stressOnset: 18,
    lethalTemp: 25.1,
    spawningMax: 13,
    migrationBlock: 21,
    thresholds: [
      { label: "Spawning/Egg", max: 13, color: "#22d3ee" },
      { label: "Juvenile Rearing", max: 16, color: "#34d399" },
      { label: "Rearing/Migration", max: 18, color: "#f59e0b" },
      { label: "Growth Cessation", max: 20.3, color: "#ef4444" },
      { label: "Lethal (UILT)", max: 25.1, color: "#991b1b" },
    ],
    keyFacts: [
      "Growth cessation above 20.3°C",
      "37% of subpopulations already thermally stressed",
      "91% stressed under 2°C warming scenario",
      "Upper incipient lethal: 25.1°C (tied with Chinook)",
      "~30% summer ocean habitat loss projected",
    ],
  },
  {
    id: "sockeye",
    name: "Sockeye Salmon",
    scientific: "Oncorhynchus nerka",
    icon: "🐟",
    color: "#f97316",
    status: "ESA Endangered (Snake River)",
    optimalRange: [7, 15],
    stressOnset: 18,
    lethalTemp: 24.4,
    spawningMax: 13,
    migrationBlock: 21,
    thresholds: [
      { label: "Spawning/Egg", max: 13, color: "#22d3ee" },
      { label: "Optimal Rearing", max: 15, color: "#34d399" },
      { label: "Migration Stress", max: 18, color: "#f59e0b" },
      { label: "CTmax Range", max: 24, color: "#ef4444" },
      { label: "Lethal (UILT)", max: 24.4, color: "#991b1b" },
    ],
    keyFacts: [
      "CTmax varies 21–25°C across Fraser populations (Eliason 2011)",
      "Population-level thermal tolerance correlated with migration difficulty",
      "Weaver Creek: 21°C CTmax (easiest route, least tolerant)",
      "Chilko: 25°C CTmax (most difficult route, most tolerant)",
      "45% summer ocean habitat loss projected",
    ],
  },
  {
    id: "eelgrass",
    name: "Eelgrass",
    scientific: "Zostera marina",
    icon: "🌿",
    color: THEME.eelgrass,
    status: "Critical habitat – nursery for 80+ species",
    optimalRange: [10, 20],
    stressOnset: 23,
    lethalTemp: 28.6,
    spawningMax: null,
    migrationBlock: null,
    thresholds: [
      { label: "Optimal Growth", max: 15.3, color: "#22d3ee" },
      { label: "Photosynthesis Peak", max: 23.3, color: "#34d399" },
      { label: "Die-back Begins", max: 25, color: "#f59e0b" },
      { label: "LT50 (50% mortality)", max: 27.6, color: "#ef4444" },
      { label: "Production Collapse", max: 28.6, color: "#991b1b" },
    ],
    keyFacts: [
      "17% of California's eelgrass in SF Bay",
      "22,800 ha in Puget Sound (43% of shoreline)",
      "Die-back begins above 23°C",
      "Wasting disease promoted by warming",
      "Nursery habitat for juvenile salmon, herring, crab",
    ],
  },
  {
    id: "srkw",
    name: "Southern Resident Killer Whale",
    scientific: "Orcinus orca",
    icon: "🐋",
    color: THEME.orca,
    status: "ESA Endangered (~73–80 individuals)",
    optimalRange: [8, 13],
    stressOnset: null,
    lethalTemp: null,
    spawningMax: null,
    migrationBlock: null,
    thresholds: [
      { label: "Core Habitat Temp", max: 13, color: "#22d3ee" },
      { label: "Noise: Ship +5–13dB", max: null, color: "#f59e0b" },
      { label: "Prey: Chinook decline", max: null, color: "#ef4444" },
    ],
    keyFacts: [
      "Population would be 30% larger without noise pollution (NBER)",
      "Ship noise raises background 5–13 dB in communication band",
      "Vessel traffic reduces hunting efficiency ~23%",
      "Primary threat: Chinook prey reduction from thermal stress",
      "Critical habitat: Haro Strait, San Juan Islands",
    ],
  },
  {
    id: "bullkelp",
    name: "Bull Kelp",
    scientific: "Nereocystis luetkeana",
    icon: "🌊",
    color: THEME.kelp,
    status: "90% loss in Sonoma/Mendocino (2014–2016 heatwave)",
    optimalRange: [8, 14],
    stressOnset: 16,
    lethalTemp: 22,
    spawningMax: null,
    migrationBlock: null,
    thresholds: [
      { label: "Growth Peak", max: 11.9, color: "#22d3ee" },
      { label: "Gametophyte Impaired", max: 16, color: "#34d399" },
      { label: "Sporophyte Stress", max: 18, color: "#f59e0b" },
      { label: "Gametophyte Lethal", max: 22, color: "#ef4444" },
    ],
    keyFacts: [
      "Growth peaks at ~11.9°C",
      "Reproduction impaired at 16°C",
      "Gametophyte lethal at 20–22°C",
      "90% loss in 2014–2016 marine heatwave",
      "Foundation species for nearshore food webs",
    ],
  },
];

export default SPECIES_DATA;
